// Flameco UI kit v2.8.0, copied by authservice ui-kit/sync.js. Do not edit; change the kit and re-sync.
'use client';
// Voice for "Search everything": record a short question in the browser, send
// it to POST {apiBase}/flameco/voice/transcribe (Whisper on the backend), get
// the words back. Recording stops by itself after a pause in speech, after
// MAX_MS, or when the person taps the mic again.
import { useCallback, useEffect, useRef, useState } from 'react';
import type { GlobalSearchConfig } from './GlobalSearch';

export type VoiceState = 'idle' | 'listening' | 'working' | 'error';

const MAX_MS = 20_000;
/** Quiet this long after speaking = done. */
const SILENCE_MS = 1_400;
/** Below this (0..1 RMS) counts as quiet. */
const QUIET = 0.02;

export function voiceSupported(): boolean {
  return typeof window !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && typeof MediaRecorder !== 'undefined';
}

export function useVoiceSearch(config: GlobalSearchConfig | undefined, onText: (text: string) => void) {
  const [state, setState] = useState<VoiceState>('idle');
  const [error, setError] = useState('');
  /** Input loudness 0..1, for the listening animation. */
  const [level, setLevel] = useState(0);
  const recRef = useRef<MediaRecorder | null>(null);
  const cleanupRef = useRef<() => void>(() => {});
  const onTextRef = useRef(onText);
  onTextRef.current = onText;

  /** Stop asked for before the microphone was ready (a quick hold-to-talk): stop as soon as it starts. */
  const stopSoonRef = useRef(false);
  const stop = useCallback(() => {
    if (recRef.current?.state === 'recording') recRef.current.stop();
    else stopSoonRef.current = true;
  }, []);

  const cancel = useCallback(() => {
    stopSoonRef.current = false;
    const rec = recRef.current;
    if (rec) rec.ondataavailable = null;
    if (rec?.state === 'recording') {
      rec.onstop = null;
      rec.stop();
    }
    cleanupRef.current();
    recRef.current = null;
    setState('idle');
    setLevel(0);
  }, []);
  useEffect(() => cancel, [cancel]);

  /** untilStop: hold-to-talk, keep recording through pauses until stop() (the key is let go). */
  const start = useCallback(async (opts?: { untilStop?: boolean }) => {
    if (!config || !voiceSupported() || recRef.current) return;
    setError('');
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
    } catch {
      setState('error');
      setError('Microphone is blocked. Allow it in the browser to search by voice.');
      return;
    }
    const type = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'].find((t) => MediaRecorder.isTypeSupported?.(t)) ?? '';
    const rec = new MediaRecorder(stream, type ? { mimeType: type } : undefined);
    recRef.current = rec;
    const chunks: Blob[] = [];
    rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);

    // Loudness, for the animation and to notice the end of speech.
    const AC: typeof AudioContext | undefined = (window as any).AudioContext || (window as any).webkitAudioContext;
    const ctx = AC ? new AC() : null;
    const analyser = ctx?.createAnalyser();
    if (ctx && analyser) {
      analyser.fftSize = 1024;
      ctx.createMediaStreamSource(stream).connect(analyser);
    }
    const buf = new Float32Array(1024);
    let spoke = false;
    let quietSince = performance.now();
    let raf = 0;
    const t0 = performance.now();
    const tick = () => {
      if (analyser) {
        analyser.getFloatTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
        const rms = Math.sqrt(sum / buf.length);
        setLevel(Math.min(1, rms * 8));
        const now = performance.now();
        if (rms > QUIET) {
          spoke = true;
          quietSince = now;
        } else if (!opts?.untilStop && spoke && now - quietSince > SILENCE_MS) {
          stop();
          return;
        }
      }
      if (performance.now() - t0 > MAX_MS) {
        stop();
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    cleanupRef.current = () => {
      cancelAnimationFrame(raf);
      stream.getTracks().forEach((t) => t.stop());
      void ctx?.close().catch(() => undefined);
    };

    rec.onstop = async () => {
      cleanupRef.current();
      recRef.current = null;
      setLevel(0);
      const blob = new Blob(chunks, { type: rec.mimeType || 'audio/webm' });
      if (blob.size < 2000) {
        setState('idle');
        return;
      }
      setState('working');
      try {
        const form = new FormData();
        form.append('audio', blob, rec.mimeType.includes('mp4') ? 'search.m4a' : 'search.webm');
        const token = config.getToken();
        const res = await fetch(`${config.apiBase.replace(/\/+$/, '')}/flameco/voice/transcribe`, {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: form,
        });
        if (!res.ok) throw new Error(String(res.status));
        const { text } = (await res.json()) as { text?: string };
        setState('idle');
        if (text?.trim()) onTextRef.current(text.trim().replace(/[.。]+$/, ''));
        else {
          setState('error');
          setError("Didn't catch that. Try again a little closer to the mic.");
        }
      } catch {
        setState('error');
        setError("Voice search isn't working right now. Type instead, or try again.");
      }
    };
    rec.start(250);
    setState('listening');
    raf = requestAnimationFrame(tick);
    if (stopSoonRef.current) {
      stopSoonRef.current = false;
      stop();
    }
  }, [config, stop]);

  const toggle = useCallback(() => (recRef.current ? stop() : void start()), [start, stop]);

  return { state, level, error, start, stop, cancel, toggle, supported: voiceSupported() };
}
